import {
	SenderSource,
	IRawMessageOut,
	MessageType,
	IRawMediaMessageIn,
	IRawVoiceMessageIn,
	IRawEventMessageIn,
	IChatEventMessage,
	IRawMessageIn,
	IRawText,
	IChatMessage_ResourceConverted,
	WithClientID,
} from '../types';
import { v4 as uuidV4 } from 'uuid';

import {
	checkRawMessageOut,
	isRawCommandMessage,
	isRawEventMessage,
	isRawMediaMessage,
	isRawTextMessage,
	isRawVoiceMessage,
} from './utils/checkRawMessage';
import {
	IChatMediaMessage,
	IChatMessage,
	IChatMessages,
	IChatTextMessage,
	IChatVoiceMessage,
} from '../types/chat/IChatMessage';
import { IChatCommandMessage } from '../types/chat/command/IChatCommandMessage';
import { IMetaResource, Resource } from './Resource';
import {
	getNormalizedIsoDate,
	isEventMessage,
	isMediaMessage,
	isTextMessage,
	isVoiceMessage,
} from '../utils';

export const ROtoCM = async (
	ro: IRawMessageOut | IRawMessageIn,
): Promise<IChatMessage> => {
	const {
		timestamp: _timestamp = undefined,
		sender = SenderSource.USER,
		client_id,
	} = { ...ro };

	const { iso: timestamp } = getNormalizedIsoDate(_timestamp);
	const _client_id = client_id ?? timestamp ?? uuidV4();

	if (isRawTextMessage(ro)) {
		const { text, ..._ro } = ro;

		const cm: IChatMessage<IChatTextMessage> = {
			_type: MessageType.TEXT,
			_data: text,
			client_id: _client_id,
			sender,
			..._ro,
			timestamp,
			_timestamp,
		};
		return cm;
	} else if (isRawMediaMessage(ro)) {
		const { media, ..._ro } = ro;

		const _data = await Resource(media);
		const cm: IChatMessage<IChatMediaMessage<IMetaResource>> = {
			_type: MessageType.MEDIA,
			_data,
			client_id: _client_id,
			sender,
			..._ro,
			timestamp,
			_timestamp,
		};
		return cm;
	} else if (isRawVoiceMessage(ro)) {
		const { voice, ..._ro } = ro;
		const _data = await Resource(voice);

		const cm: IChatMessage<IChatVoiceMessage<IMetaResource>> = {
			_type: MessageType.VOICE,
			_data,
			client_id: _client_id,
			sender,
			..._ro,
			timestamp,
			_timestamp,
		};
		return cm;
	} else if (checkRawMessageOut(ro) && isRawCommandMessage(ro)) {
		const { command, parameters, ..._ro } =
			'parameters' in ro ? ro : { parameters: undefined, ...ro };

		const cm: IChatMessage<IChatCommandMessage> = {
			_type: MessageType.COMMAND,
			_data: { name: command, parameters } as IChatCommandMessage['_data'],
			client_id: _client_id,
			..._ro,
			sender,
			timestamp,
			_timestamp,
		};
		return cm;
	} else if (isRawEventMessage(ro)) {
		const { event, parameters, ..._ro } =
			'parameters' in ro ? ro : { parameters: undefined, ...ro };

		const cm: IChatMessage<IChatEventMessage> = {
			_type: MessageType.EVENT,
			_data: { name: event, parameters } as IChatEventMessage['_data'],
			sender,
			..._ro,
			timestamp,
			client_id: _client_id,
			_timestamp,
		};
		return cm;
	} else {
		throw new Error('Wrong data');
	}
};

export const CMOtoRI = <
	T extends WithClientID<
		IChatMessage_ResourceConverted<Exclude<IChatMessages, IChatCommandMessage>>
	>,
>(
	message: T,
) => {
	const { client_id } = message;
	if (isTextMessage(message)) {
		const { _data, metadata } = message;
		const ri: IRawMessageIn<IRawText> = {
			text: _data,
			client_id,
			metadata,
		};
		return ri;
	} else if (isMediaMessage(message)) {
		const {
			_data: { resource_id },
		} = message;
		const ri: IRawMessageIn<IRawMediaMessageIn> = {
			media: { resource_id },
			client_id,
		};
		return ri;
	} else if (isVoiceMessage(message) && message._data.resource_id) {
		const {
			_data: { resource_id },
		} = message;
		const ri: IRawMessageIn<IRawVoiceMessageIn> = {
			voice: { resource_id },
			client_id,
		};
		return ri;
	} else if (isEventMessage(message)) {
		const {
			_data: { name, parameters },
		} = message;

		const ri: IRawMessageIn<IRawEventMessageIn> = {
			event: name,
			client_id,
			parameters,
		} as IRawMessageIn<IRawEventMessageIn>;
		return ri;
	} else {
		throw Error('Invalid message type');
	}
};
